import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { reportsAPI, masterAPI } from '../../services/api';

const T = {
  bg: 'var(--bg)', surface: 'var(--surface)', border: 'var(--border)',
  accent: 'var(--accent)', muted: 'var(--muted)', text: 'var(--text)',
  g50: 'var(--accent-bg)', g100: 'var(--accent-bg)',
};

const COLORS = ['var(--accent)','#10b981','#f59e0b','#ef4444','#8b5cf6','#06b6d4','#f97316','#ec4899','#6366f1','#14b8a6'];
const RESOLVED_STATUS = 'Completed,Pending User Acceptance,Resolved,Closed';

function buildTicketsUrl(from, to, params = {}) {
  const p = new URLSearchParams();
  if (from) p.set('from', from);
  if (to) p.set('to', to);
  Object.entries(params).forEach(([k, v]) => {
    if (v != null && v !== '') p.set(k, String(v));
  });
  return `/tickets?${p.toString()}`;
}

const clickableStyle = (enabled) => enabled ? {
  cursor: 'pointer',
  transition: 'transform 0.12s ease, box-shadow 0.12s ease',
} : {};

const clickableHover = (enabled, enter) => {
  if (!enabled) return undefined;
  return (e) => {
    if (enter) {
      e.currentTarget.style.transform = 'translateY(-2px)';
      e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.08)';
    } else {
      e.currentTarget.style.transform = '';
      e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.06)';
    }
  };
};

// ─── Tiny chart helpers ────────────────────────────────────────────────────────
function BarChart({ data = [], color = 'var(--accent)', height = 160, onItemClick }) {
  if (!data.length) return <Empty />;
  const max = Math.max(...data.map(d => Number(d.value) || 0), 1);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height, padding: '0 4px' }}>
      {data.map((d, i) => {
        const pct = (Number(d.value) || 0) / max;
        const clickable = !!onItemClick && Number(d.value) > 0;
        return (
          <div
            key={i}
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            onClick={clickable ? () => onItemClick(d) : undefined}
            onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onItemClick(d); } } : undefined}
            style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, height: '100%', justifyContent: 'flex-end', ...clickableStyle(clickable) }}
            title={clickable ? `View ${d.label} tickets` : `${d.label}: ${d.value}`}
          >
            <span style={{ fontSize: 9, color: T.muted, fontWeight: 700 }}>{d.value}</span>
            <div style={{ width: '100%', height: `${Math.max(pct * (height - 28), 4)}px`, background: COLORS[i % COLORS.length], borderRadius: '4px 4px 0 0', transition: 'height 0.4s ease, opacity 0.12s', opacity: clickable ? 0.85 : 0.85 }} />
            <span style={{ fontSize: 9, color: T.muted, textAlign: 'center', maxWidth: 60, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.label}</span>
          </div>
        );
      })}
    </div>
  );
}

function DonutChart({ data = [], size = 130, onItemClick }) {
  if (!data.length) return <Empty />;
  const total = data.reduce((s, d) => s + (Number(d.value) || 0), 0) || 1;
  let offset = 0;
  const r = 45, cx = 65, cy = 65, circ = 2 * Math.PI * r;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
      <svg width={size} height={size} viewBox="0 0 130 130">
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#F0F2F5" strokeWidth="18" />
        {data.map((d, i) => {
          const pct = (Number(d.value) || 0) / total;
          const dash = pct * circ;
          const gap  = circ - dash;
          const clickable = !!onItemClick && Number(d.value) > 0;
          const seg  = (
            <circle
              key={i}
              cx={cx}
              cy={cy}
              r={r}
              fill="none"
              stroke={COLORS[i % COLORS.length]}
              strokeWidth="18"
              strokeDasharray={`${dash} ${gap}`}
              strokeDashoffset={-offset * circ}
              style={{
                transform: 'rotate(-90deg)',
                transformOrigin: '65px 65px',
                transition: 'stroke-dasharray 0.4s ease, opacity 0.12s',
                cursor: clickable ? 'pointer' : 'default',
                opacity: clickable ? 0.9 : 0.85,
              }}
              onClick={clickable ? () => onItemClick(d) : undefined}
            >
              <title>{clickable ? `View ${d.label} tickets (${d.value})` : `${d.label}: ${d.value}`}</title>
            </circle>
          );
          offset += pct;
          return seg;
        })}
        <text x={cx} y={cy - 6} textAnchor="middle" fontSize="18" fontWeight="800" fill={T.text}>{total}</text>
        <text x={cx} y={cy + 14} textAnchor="middle" fontSize="10" fill={T.muted}>Total</text>
      </svg>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        {data.slice(0, 6).map((d, i) => {
          const clickable = !!onItemClick && Number(d.value) > 0;
          return (
            <div
              key={i}
              role={clickable ? 'button' : undefined}
              tabIndex={clickable ? 0 : undefined}
              onClick={clickable ? () => onItemClick(d) : undefined}
              onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onItemClick(d); } } : undefined}
              style={{ display: 'flex', alignItems: 'center', gap: 6, ...clickableStyle(clickable), borderRadius: 6, padding: '2px 4px' }}
              title={clickable ? `View ${d.label} tickets` : undefined}
            >
              <div style={{ width: 10, height: 10, borderRadius: 3, background: COLORS[i % COLORS.length], flexShrink: 0 }} />
              <span style={{ fontSize: 11, color: T.text, fontWeight: 600 }}>{d.label}</span>
              <span style={{ fontSize: 11, color: T.muted, marginLeft: 'auto' }}>{d.value}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function LineChart({ data = [], height = 140, onItemClick }) {
  if (!data.length) return <Empty />;
  const maxC = Math.max(...data.map(d => Number(d.created) || 0), 1);
  const maxR = Math.max(...data.map(d => Number(d.resolved) || 0), 1);
  const max  = Math.max(maxC, maxR, 1);
  const w = 500, h = height;
  const pts = (key) => data.map((d, i) => {
    const x = (i / Math.max(data.length - 1, 1)) * (w - 40) + 20;
    const y = h - 20 - ((Number(d[key]) || 0) / max) * (h - 30);
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg viewBox={`0 0 ${w} ${h}`} style={{ width: '100%', height }} preserveAspectRatio="none">
      <polyline fill="none" stroke="var(--accent)" strokeWidth="2.5" points={pts('created')} strokeLinejoin="round" strokeLinecap="round" />
      <polyline fill="none" stroke="#10b981" strokeWidth="2.5" points={pts('resolved')} strokeLinejoin="round" strokeLinecap="round" />
      {data.map((d, i) => {
        const x = (i / Math.max(data.length - 1, 1)) * (w - 40) + 20;
        const yC = h - 20 - ((Number(d.created) || 0) / max) * (h - 30);
        const yR = h - 20 - ((Number(d.resolved) || 0) / max) * (h - 30);
        const clickable = !!onItemClick && d.date;
        return (
          <g key={i}>
            <circle cx={x} cy={yC} r={clickable ? 6 : 3.5} fill="var(--accent)" style={{ cursor: clickable ? 'pointer' : 'default' }} onClick={clickable ? () => onItemClick(d, 'created') : undefined}>
              {clickable && <title>View tickets created on {d.date}</title>}
            </circle>
            <circle cx={x} cy={yR} r={clickable ? 6 : 3.5} fill="#10b981" style={{ cursor: clickable ? 'pointer' : 'default' }} onClick={clickable ? () => onItemClick(d, 'resolved') : undefined}>
              {clickable && <title>View tickets resolved on {d.date}</title>}
            </circle>
          </g>
        );
      })}
    </svg>
  );
}

function HBar({ data = [], height = 28, onItemClick, onMetricClick }) {
  if (!data.length) return <Empty />;
  const max = Math.max(...data.map(d => Number(d.assigned) || 0), 1);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {data.slice(0, 8).map((d, i) => {
        const clickable = !!onItemClick;
        return (
          <div
            key={i}
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            onClick={clickable ? () => onItemClick(d) : undefined}
            onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onItemClick(d); } } : undefined}
            style={{ display: 'flex', alignItems: 'center', gap: 10, borderRadius: 8, padding: '4px 6px', ...clickableStyle(clickable) }}
            title={clickable ? `View tickets for ${d.name}` : undefined}
          >
            <span style={{ width: 110, fontSize: 11, fontWeight: 600, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 0 }} title={d.name}>{d.name}</span>
            <div style={{ flex: 1, background: T.g50, borderRadius: 6, height: height, overflow: 'hidden', position: 'relative' }}>
              <div style={{ width: `${(Number(d.resolved) || 0) / max * 100}%`, height: '100%', background: 'linear-gradient(90deg, var(--accent), var(--accent-light))', borderRadius: 6, transition: 'width 0.5s ease' }} />
            </div>
            <div style={{ display: 'flex', gap: 8, fontSize: 11, flexShrink: 0 }}>
              <span
                style={{ color: T.accent, fontWeight: 700, cursor: onMetricClick ? 'pointer' : 'default', textDecoration: onMetricClick ? 'underline' : 'none' }}
                onClick={onMetricClick ? (e) => { e.stopPropagation(); onMetricClick(d, 'resolved'); } : undefined}
                title={onMetricClick ? 'View resolved tickets' : undefined}
              >{d.resolved ?? 0}✓</span>
              <span
                style={{ color: T.muted, cursor: onMetricClick ? 'pointer' : 'default', textDecoration: onMetricClick ? 'underline' : 'none' }}
                onClick={onMetricClick ? (e) => { e.stopPropagation(); onMetricClick(d, 'open'); } : undefined}
                title={onMetricClick ? 'View open tickets' : undefined}
              >{d.open ?? 0} open</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Empty() {
  return <div style={{ textAlign: 'center', padding: '24px 0', color: T.muted, fontSize: 13 }}>No data for selected period</div>;
}

// ─── Card wrapper ─────────────────────────────────────────────────────────────
function Card({ title, subtitle, children, action, span = 1 }) {
  return (
    <div style={{
      background: T.surface, border: `1px solid ${T.border}`,
      borderRadius: 14, padding: '18px 20px',
      boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
      gridColumn: `span ${span}`,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 13.5, fontWeight: 800, color: T.text }}>{title}</div>
          {subtitle && <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>{subtitle}</div>}
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

// ─── Stat tile ────────────────────────────────────────────────────────────────
function StatTile({ label, value, color = 'var(--accent)', icon, sub, onClick }) {
  const clickable = !!onClick;
  return (
    <div
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={onClick}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
      title={clickable ? `View ${label.toLowerCase()} tickets` : undefined}
      style={{
        background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: '16px 20px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        ...clickableStyle(clickable),
      }}
      onMouseEnter={clickableHover(clickable, true)}
      onMouseLeave={clickableHover(clickable, false)}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</span>
        <span style={{ fontSize: 18 }}>{icon}</span>
      </div>
      <div style={{ fontSize: 32, fontWeight: 900, color, letterSpacing: '-1px', lineHeight: 1 }}>{value ?? '—'}</div>
      {sub && <div style={{ fontSize: 11, color: T.muted, marginTop: 6 }}>{sub}</div>}
    </div>
  );
}

// ─── Filter bar ──────────────────────────────────────────────────────────────
const DATE_PRESETS = [
  { label: 'Today',       days: 0  },
  { label: 'Last 7 days', days: 7  },
  { label: 'Last 30 days',days: 30 },
  { label: 'Last 90 days',days: 90 },
  { label: 'This month',  days: -1 },
];

function toISO(date) { return date.toISOString().slice(0, 10); }

function getPresetRange(days) {
  const now = new Date();
  if (days === 0)  return { from: toISO(now), to: toISO(now) };
  if (days === -1) return { from: toISO(new Date(now.getFullYear(), now.getMonth(), 1)), to: toISO(now) };
  return { from: toISO(new Date(now - days * 86400000)), to: toISO(now) };
}

// ─── Custom Report Builder ────────────────────────────────────────────────────
const GROUP_OPTIONS = [
  { value: 'status',     label: 'Status' },
  { value: 'priority',   label: 'Priority' },
  { value: 'department', label: 'Department' },
  { value: 'source',     label: 'Source' },
  { value: 'type',       label: 'Type' },
];
const METRIC_OPTIONS = [
  { value: 'count',           label: 'Ticket Count' },
  { value: 'avg_resolution',  label: 'Avg Resolution Time (hrs)' },
];
const CHART_OPTIONS = [
  { value: 'bar',   label: '▊ Bar Chart' },
  { value: 'donut', label: '◎ Donut Chart' },
];

function CustomReportBuilder({ departments }) {
  const navigate = useNavigate();
  const [config, setConfig] = useState({ group_by: 'status', metric: 'count', chart: 'bar', days: 30 });
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(false);

  const run = useCallback(async () => {
    setLoading(true);
    const { from, to } = getPresetRange(config.days);
    try {
      const res = await reportsAPI.custom({ from, to, group_by: config.group_by, metric: config.metric });
      setData(res.data || []);
    } catch { setData([]); }
    setLoading(false);
  }, [config]);

  useEffect(() => { run(); }, []);

  const exportCSV = () => {
    if (!data?.length) return;
    const csv = 'Label,Value\n' + data.map(d => `"${d.label}","${d.value}"`).join('\n');
    const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `custom_report_${config.group_by}.csv`; a.click();
  };

  const handleRowClick = (row) => {
    if (!Number(row.value)) return;
    const { from, to } = getPresetRange(config.days);
    const label = row.label || '';
    const map = {
      status:     { status: label },
      priority:   { priority: label },
      department: { department: label === 'Unspecified' ? 'Unspecified' : label },
      source:     { source: label },
      type:       { type: label },
    };
    const params = map[config.group_by];
    if (params) navigate(buildTicketsUrl(from, to, params));
  };

  const sel = (key, v) => setConfig(c => ({ ...c, [key]: v }));
  const selStyle = { padding: '7px 12px', borderRadius: 8, border: `1px solid ${T.border}`, background: T.surface, fontSize: 12, color: T.text, cursor: 'pointer', outline: 'none' };

  return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: '20px 24px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
      <div style={{ fontSize: 14, fontWeight: 800, color: T.text, marginBottom: 16 }}>🛠 Custom Report Builder</div>

      {/* Controls */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 20, alignItems: 'flex-end' }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: T.muted, marginBottom: 4 }}>GROUP BY</div>
          <select value={config.group_by} onChange={e => sel('group_by', e.target.value)} style={selStyle}>
            {GROUP_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: T.muted, marginBottom: 4 }}>METRIC</div>
          <select value={config.metric} onChange={e => sel('metric', e.target.value)} style={selStyle}>
            {METRIC_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: T.muted, marginBottom: 4 }}>CHART TYPE</div>
          <select value={config.chart} onChange={e => sel('chart', e.target.value)} style={selStyle}>
            {CHART_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: T.muted, marginBottom: 4 }}>PERIOD</div>
          <select value={config.days} onChange={e => sel('days', Number(e.target.value))} style={selStyle}>
            {DATE_PRESETS.map(p => <option key={p.days} value={p.days}>{p.label}</option>)}
          </select>
        </div>
        <button onClick={run} style={{ padding: '8px 20px', background: 'linear-gradient(135deg, var(--accent), var(--accent-dark))', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 12px var(--accent-bg)' }}>
          Run Report
        </button>
        {data?.length > 0 && (
          <button onClick={exportCSV} style={{ padding: '8px 16px', background: T.surface, color: T.accent, border: `1.5px solid ${T.accent}`, borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
            ↓ Export CSV
          </button>
        )}
      </div>

      {/* Chart result */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: T.muted }}>Loading…</div>
      ) : data === null ? null : (
        <div style={{ minHeight: 200 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: T.muted, marginBottom: 12 }}>
            {METRIC_OPTIONS.find(m => m.value === config.metric)?.label} by {GROUP_OPTIONS.find(g => g.value === config.group_by)?.label}
          </div>
          {config.chart === 'bar'
            ? <BarChart data={data} height={180} onItemClick={handleRowClick} />
            : <DonutChart data={data} size={140} onItemClick={handleRowClick} />
          }
          {/* Data table */}
          <div style={{ marginTop: 20, borderRadius: 10, border: `1px solid ${T.border}`, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: T.g50 }}>
                  <th style={{ padding: '8px 14px', textAlign: 'left', fontWeight: 800, color: T.muted, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {GROUP_OPTIONS.find(g => g.value === config.group_by)?.label}
                  </th>
                  <th style={{ padding: '8px 14px', textAlign: 'right', fontWeight: 800, color: T.muted, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {METRIC_OPTIONS.find(m => m.value === config.metric)?.label}
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.map((row, i) => (
                  <tr
                    key={i}
                    role={Number(row.value) > 0 ? 'button' : undefined}
                    tabIndex={Number(row.value) > 0 ? 0 : undefined}
                    onClick={Number(row.value) > 0 ? () => handleRowClick(row) : undefined}
                    onKeyDown={Number(row.value) > 0 ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleRowClick(row); } } : undefined}
                    style={{
                      borderTop: `1px solid ${T.border}`,
                      background: i % 2 ? T.g50 : T.surface,
                      cursor: Number(row.value) > 0 ? 'pointer' : 'default',
                    }}
                    title={Number(row.value) > 0 ? `View ${row.label} tickets` : undefined}
                  >
                    <td style={{ padding: '8px 14px', color: T.text, fontWeight: 600 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 10, height: 10, borderRadius: 3, background: COLORS[i % COLORS.length] }} />
                        {row.label}
                      </div>
                    </td>
                    <td style={{ padding: '8px 14px', textAlign: 'right', color: T.accent, fontWeight: 800 }}>{row.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Reports Page ────────────────────────────────────────────────────────
const BUILT_IN_REPORTS = [
  { id: 'overview',  icon: '📊', label: 'Overview',          desc: 'Total tickets, resolution rate and key metrics' },
  { id: 'sla',       icon: '⏱',  label: 'SLA Compliance',    desc: 'Breached, at-risk and within-SLA tickets' },
  { id: 'agent',     icon: '👥',  label: 'Agent Performance', desc: 'Assigned, resolved and open per agent' },
  { id: 'trend',     icon: '📈',  label: 'Ticket Trends',     desc: 'Daily created vs resolved over time' },
  { id: 'dept',      icon: '🏢',  label: 'By Department',     desc: 'Ticket distribution per department' },
  { id: 'custom',    icon: '🛠',  label: 'Custom Builder',    desc: 'Build your own report with any dimension' },
];

export default function ReportsPage() {
  const navigate = useNavigate();
  const [activeReport, setActiveReport] = useState('overview');
  const [data, setData]                 = useState(null);
  const [loading, setLoading]           = useState(false);
  const [preset, setPreset]             = useState(30);
  const [customFrom, setCustomFrom]     = useState('');
  const [customTo, setCustomTo]         = useState('');
  const [departments, setDepartments]   = useState([]);

  const { from, to } = customFrom && customTo
    ? { from: customFrom, to: customTo }
    : getPresetRange(preset ?? 30);

  const goTickets = useCallback((params = {}) => {
    navigate(buildTicketsUrl(from, to, params));
  }, [navigate, from, to]);

  const fetchData = useCallback(async () => {
    if (activeReport === 'custom') return;
    setLoading(true);
    try {
      const res = await reportsAPI.summary({ from, to });
      setData(res);
    } catch { setData(null); }
    setLoading(false);
  }, [from, to, activeReport]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    masterAPI.list('Department').then(items => setDepartments(items.map(i => i.value))).catch(() => {});
  }, []);

  const exportCSV = () => {
    if (!data) return;
    const rows = [
      ['Metric', 'Value'],
      ['Total Tickets', data.totals?.total],
      ['Open', data.totals?.open],
      ['Resolved', data.totals?.resolved],
      ['Overdue', data.totals?.overdue],
      ['Unassigned', data.totals?.unassigned],
      ['SLA Compliance %', data.sla?.compliance_pct],
      ['Avg Resolution (hrs)', data.totals?.avg_resolution_hours],
    ];
    const csv = rows.map(r => r.join(',')).join('\n');
    const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `report_${from}_${to}.csv`; a.click();
  };

  return (
    <div style={{ display: 'flex', height: '100%', background: T.bg, fontFamily: "'DM Sans', system-ui, sans-serif", overflow: 'hidden' }}>

      {/* ── Left sidebar: report nav ── */}
      <div style={{ width: 240, background: T.surface, borderRight: `1px solid ${T.border}`, display: 'flex', flexDirection: 'column', flexShrink: 0, overflowY: 'auto' }}>
        <div style={{ padding: '16px 16px 10px', fontSize: 10, fontWeight: 800, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Reports</div>
        {BUILT_IN_REPORTS.map(r => {
          const isActive = activeReport === r.id;
          return (
            <button
              key={r.id}
              onClick={() => setActiveReport(r.id)}
              style={{
                width: '100%', textAlign: 'left', padding: '10px 16px',
                border: 'none',
                borderLeft: isActive ? `3px solid ${T.accent}` : '3px solid transparent',
                background: isActive ? T.g50 : 'transparent',
                cursor: 'pointer', display: 'flex', alignItems: 'flex-start', gap: 10,
                transition: 'background 0.12s',
              }}
              onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = '#F8FAFF'; }}
              onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
            >
              <span style={{ fontSize: 15, marginTop: 1, flexShrink: 0 }}>{r.icon}</span>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 12.5, fontWeight: isActive ? 800 : 600, color: isActive ? T.accent : T.text, lineHeight: 1.3 }}>{r.label}</div>
                <div style={{ fontSize: 10.5, color: T.muted, marginTop: 2, lineHeight: 1.4, whiteSpace: 'normal' }}>{r.desc}</div>
              </div>
            </button>
          );
        })}
      </div>

      {/* ── Main content ── */}
      <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>

        {/* Topbar — two rows: title row + filter row */}
        <div style={{ background: T.surface, borderBottom: `1px solid ${T.border}`, padding: '10px 24px', flexShrink: 0 }}>
          {/* Row 1: Title + export */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 18 }}>{BUILT_IN_REPORTS.find(r => r.id === activeReport)?.icon}</span>
              <div>
                <div style={{ fontSize: 15, fontWeight: 800, color: T.text, lineHeight: 1.2 }}>
                  {BUILT_IN_REPORTS.find(r => r.id === activeReport)?.label}
                </div>
                <div style={{ fontSize: 11, color: T.muted }}>
                  {from} → {to}
                </div>
              </div>
            </div>
            {activeReport !== 'custom' && (
              <button onClick={exportCSV} style={{ padding: '7px 16px', background: T.surface, color: T.accent, border: `1.5px solid ${T.accent}`, borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                ↓ Export CSV
              </button>
            )}
          </div>
          {/* Row 2: Date presets + custom range */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'nowrap' }}>
            {DATE_PRESETS.map(p => {
              const isActive = preset === p.days && !customFrom;
              return (
                <button key={p.days} onClick={() => { setPreset(p.days); setCustomFrom(''); setCustomTo(''); }}
                  style={{ padding: '4px 12px', borderRadius: 20, border: `1.5px solid ${isActive ? T.accent : T.border}`, background: isActive ? T.g50 : T.surface, fontSize: 11, fontWeight: 600, color: isActive ? T.accent : T.muted, cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.15s' }}>
                  {p.label}
                </button>
              );
            })}
            <div style={{ width: 1, height: 20, background: T.border, margin: '0 4px' }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, padding: '3px 10px' }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={T.muted} strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              <input type="date" value={customFrom} onChange={e => { setCustomFrom(e.target.value); setPreset(null); }}
                style={{ border: 'none', background: 'transparent', fontSize: 11, color: T.text, outline: 'none', cursor: 'pointer', width: 110 }} />
              <span style={{ fontSize: 11, color: T.muted }}>—</span>
              <input type="date" value={customTo} onChange={e => { setCustomTo(e.target.value); setPreset(null); }}
                style={{ border: 'none', background: 'transparent', fontSize: 11, color: T.text, outline: 'none', cursor: 'pointer', width: 110 }} />
            </div>
          </div>
        </div>

        <div style={{ flex: 1, padding: 24 }}>
          {activeReport === 'custom' ? (
            <CustomReportBuilder departments={departments} />
          ) : loading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, color: T.muted, fontSize: 14 }}>Loading report data…</div>
          ) : !data ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, color: T.muted, fontSize: 14 }}>Failed to load. Check backend connection.</div>
          ) : (
            <>
              {/* ── OVERVIEW ── */}
              {activeReport === 'overview' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
                    <StatTile label="Total Tickets" value={data.totals?.total} icon="🎫" color={T.accent} sub={`${from} → ${to}`} onClick={() => goTickets()} />
                    <StatTile label="Open" value={data.totals?.open} icon="📂" color="#f59e0b" sub="Not yet resolved" onClick={() => goTickets({ view: 'unresolved' })} />
                    <StatTile label="Resolved" value={data.totals?.resolved} icon="✅" color="#10b981" sub="Successfully closed" onClick={() => goTickets({ status: RESOLVED_STATUS })} />
                    <StatTile label="Overdue" value={data.totals?.overdue} icon="⚠️" color="#ef4444" sub="Past due date" onClick={() => goTickets({ overdue: '1' })} />
                    <StatTile label="Unassigned" value={data.totals?.unassigned} icon="👤" color="#8b5cf6" sub="No agent assigned" onClick={() => goTickets({ unassigned: '1', view: 'unresolved' })} />
                    <StatTile label="SLA Compliance" value={`${data.sla?.compliance_pct ?? 0}%`} icon="⏱" color="#06b6d4" sub={`${data.sla?.breached ?? 0} breached`} onClick={() => goTickets({ sla_status: 'within' })} />
                    <StatTile label="Avg Resolution" value={data.totals?.avg_resolution_hours ? `${data.totals.avg_resolution_hours}h` : '—'} icon="⏰" color="#6366f1" sub="Average per ticket" onClick={() => goTickets({ status: RESOLVED_STATUS })} />
                    <StatTile label="SLA Breached" value={data.sla?.breached ?? 0} icon="🚨" color="#ef4444" sub="Needs attention" onClick={() => goTickets({ sla_status: 'breached' })} />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                    <Card title="Tickets by Status" subtitle="Click a segment to filter tickets">
                      <DonutChart
                        data={data.byStatus?.map(d => ({ label: d.status, value: d.count })) || []}
                        onItemClick={(d) => goTickets({ status: d.label })}
                      />
                    </Card>
                    <Card title="Tickets by Priority" subtitle="Click a bar to filter tickets">
                      <BarChart
                        data={data.byPriority?.map(d => ({ label: d.priority, value: d.count })) || []}
                        onItemClick={(d) => goTickets({ priority: d.label })}
                      />
                    </Card>
                    <Card title="Tickets by Source" subtitle="Click a bar to filter tickets">
                      <BarChart
                        data={data.bySource?.map(d => ({ label: d.source, value: d.count })) || []}
                        onItemClick={(d) => goTickets({ source: d.label })}
                      />
                    </Card>
                    <Card title="Tickets by Type" subtitle="Click a segment to filter tickets">
                      <DonutChart
                        data={data.byType?.map(d => ({ label: d.type, value: d.count })) || []}
                        onItemClick={(d) => goTickets({ type: d.label })}
                      />
                    </Card>
                  </div>
                </div>
              )}

              {/* ── SLA ── */}
              {activeReport === 'sla' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
                    <StatTile label="Total Tickets" value={data.sla?.total} icon="🎫" color={T.accent} onClick={() => goTickets()} />
                    <StatTile label="Within SLA" value={data.sla?.within_sla} icon="✅" color="#10b981" sub="On track" onClick={() => goTickets({ sla_status: 'within' })} />
                    <StatTile label="At Risk" value={data.sla?.at_risk} icon="⚠️" color="#f59e0b" sub="Monitor closely" onClick={() => goTickets({ sla_status: 'at_risk' })} />
                    <StatTile label="Breached" value={data.sla?.breached} icon="🚨" color="#ef4444" sub="Action needed" onClick={() => goTickets({ sla_status: 'breached' })} />
                  </div>
                  <Card title="SLA Compliance Breakdown" subtitle="Click a segment to view matching tickets">
                    <DonutChart
                      data={[
                        { label: 'Within SLA', value: data.sla?.within_sla || 0 },
                        { label: 'At Risk',    value: data.sla?.at_risk    || 0 },
                        { label: 'Breached',   value: data.sla?.breached   || 0 },
                      ]}
                      size={160}
                      onItemClick={(d) => {
                        const map = { 'Within SLA': 'within', 'At Risk': 'at_risk', Breached: 'breached' };
                        if (map[d.label]) goTickets({ sla_status: map[d.label] });
                      }}
                    />
                  </Card>
                  <Card title="Compliance Rate" subtitle="Click a row to view tickets">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {[
                        { label: 'Within SLA', val: data.sla?.within_sla || 0, color: '#10b981', sla: 'within' },
                        { label: 'At Risk',    val: data.sla?.at_risk    || 0, color: '#f59e0b', sla: 'at_risk' },
                        { label: 'Breached',   val: data.sla?.breached   || 0, color: '#ef4444', sla: 'breached' },
                      ].map(item => {
                        const total = data.sla?.total || 1;
                        const pct = Math.round((item.val / total) * 100);
                        const clickable = item.val > 0;
                        return (
                          <div
                            key={item.label}
                            role={clickable ? 'button' : undefined}
                            tabIndex={clickable ? 0 : undefined}
                            onClick={clickable ? () => goTickets({ sla_status: item.sla }) : undefined}
                            onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); goTickets({ sla_status: item.sla }); } } : undefined}
                            style={{ borderRadius: 8, padding: '4px 6px', ...clickableStyle(clickable) }}
                            title={clickable ? `View ${item.label.toLowerCase()} tickets` : undefined}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, fontWeight: 600, color: T.text, marginBottom: 4 }}>
                              <span>{item.label}</span><span>{item.val} ({pct}%)</span>
                            </div>
                            <div style={{ height: 10, background: T.g50, borderRadius: 99, overflow: 'hidden' }}>
                              <div style={{ width: `${pct}%`, height: '100%', background: item.color, borderRadius: 99, transition: 'width 0.5s ease' }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </Card>
                </div>
              )}

              {/* ── AGENT PERFORMANCE ── */}
              {activeReport === 'agent' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                  <Card title="Agent Performance" subtitle={`${from} → ${to} · Click an agent to view their tickets`}>
                    <HBar
                      data={data.agentPerf || []}
                      onItemClick={(a) => goTickets({ assignee: a.name })}
                      onMetricClick={(a, metric) => goTickets({
                        assignee: a.name,
                        ...(metric === 'resolved' ? { status: RESOLVED_STATUS } : { view: 'unresolved' }),
                      })}
                    />
                  </Card>
                  <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                      <thead>
                        <tr style={{ background: T.g50 }}>
                          {['Agent', 'Assigned', 'Resolved', 'Open', 'SLA Breached', 'Avg Resolution'].map(h => (
                            <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 800, color: T.muted, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(data.agentPerf || []).map((a, i) => (
                          <tr key={i} style={{ borderTop: `1px solid ${T.border}` }}>
                            <td
                              style={{ padding: '10px 16px', fontWeight: 700, color: T.accent, cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 2 }}
                              onClick={() => goTickets({ assignee: a.name })}
                              title={`View all tickets for ${a.name}`}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <div style={{ width: 28, height: 28, borderRadius: '50%', background: `linear-gradient(135deg,${COLORS[i % COLORS.length]},${COLORS[(i+1) % COLORS.length]})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#fff', fontWeight: 800 }}>{(a.name || '?')[0]}</div>
                                {a.name}
                              </div>
                            </td>
                            <td style={{ padding: '10px 16px', color: T.text, cursor: 'pointer' }} onClick={() => goTickets({ assignee: a.name })} title="View assigned">{a.assigned}</td>
                            <td style={{ padding: '10px 16px', color: '#10b981', fontWeight: 700, cursor: 'pointer' }} onClick={() => goTickets({ assignee: a.name, status: RESOLVED_STATUS })} title="View resolved">{a.resolved}</td>
                            <td style={{ padding: '10px 16px', color: '#f59e0b', cursor: 'pointer' }} onClick={() => goTickets({ assignee: a.name, view: 'unresolved' })} title="View open">{a.open}</td>
                            <td style={{ padding: '10px 16px', color: '#ef4444', cursor: 'pointer' }} onClick={() => goTickets({ assignee: a.name, sla_status: 'breached' })} title="View SLA breached">{a.sla_breached}</td>
                            <td style={{ padding: '10px 16px', color: T.muted }}>{a.avg_resolution_hrs ? `${a.avg_resolution_hrs}h` : '—'}</td>
                          </tr>
                        ))}
                        {!data.agentPerf?.length && (
                          <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: T.muted }}>No agent data</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* ── TREND ── */}
              {activeReport === 'trend' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14 }}>
                    <StatTile label="Total Created" value={data.trend?.reduce((s, d) => s + Number(d.created), 0)} icon="📥" color={T.accent} onClick={() => goTickets()} />
                    <StatTile label="Total Resolved" value={data.trend?.reduce((s, d) => s + Number(d.resolved), 0)} icon="✅" color="#10b981" onClick={() => goTickets({ status: RESOLVED_STATUS })} />
                    <StatTile label="Pending Backlog" value={Number(data.totals?.open)} icon="📂" color="#f59e0b" onClick={() => goTickets({ view: 'unresolved' })} />
                  </div>
                  <Card title="Daily Ticket Volume" subtitle="Click a point to view tickets for that day">
                    <div style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}><div style={{ width: 12, height: 3, background: '#3B5BDB', borderRadius: 2 }} /><span style={{ fontSize: 11, color: T.muted }}>Created</span></div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}><div style={{ width: 12, height: 3, background: '#10b981', borderRadius: 2 }} /><span style={{ fontSize: 11, color: T.muted }}>Resolved</span></div>
                    </div>
                    <LineChart
                      data={data.trend || []}
                      height={200}
                      onItemClick={(d, kind) => {
                        const day = String(d.date).slice(0, 10);
                        goTickets({
                          from: day,
                          to: day,
                          ...(kind === 'resolved' ? { status: RESOLVED_STATUS } : {}),
                        });
                      }}
                    />
                  </Card>
                  <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                      <thead>
                        <tr style={{ background: T.g50 }}>
                          {['Date','Created','Resolved','Net Change'].map(h => (
                            <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 800, color: T.muted, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(data.trend || []).slice(-15).reverse().map((d, i) => {
                          const net = Number(d.created) - Number(d.resolved);
                          const day = String(d.date).slice(0, 10);
                          return (
                            <tr key={i} style={{ borderTop: `1px solid ${T.border}` }}>
                              <td style={{ padding: '9px 16px', fontWeight: 600, color: T.text }}>{d.date}</td>
                              <td
                                style={{ padding: '9px 16px', color: T.accent, fontWeight: 700, cursor: Number(d.created) > 0 ? 'pointer' : 'default', textDecoration: Number(d.created) > 0 ? 'underline' : 'none' }}
                                onClick={Number(d.created) > 0 ? () => goTickets({ from: day, to: day }) : undefined}
                                title={Number(d.created) > 0 ? 'View tickets created this day' : undefined}
                              >{d.created}</td>
                              <td
                                style={{ padding: '9px 16px', color: '#10b981', fontWeight: 700, cursor: Number(d.resolved) > 0 ? 'pointer' : 'default', textDecoration: Number(d.resolved) > 0 ? 'underline' : 'none' }}
                                onClick={Number(d.resolved) > 0 ? () => goTickets({ from: day, to: day, status: RESOLVED_STATUS }) : undefined}
                                title={Number(d.resolved) > 0 ? 'View tickets resolved this day' : undefined}
                              >{d.resolved}</td>
                              <td style={{ padding: '9px 16px', color: net > 0 ? '#ef4444' : '#10b981', fontWeight: 700 }}>{net > 0 ? `+${net}` : net}</td>
                            </tr>
                          );
                        })}
                        {!data.trend?.length && <tr><td colSpan={4} style={{ padding: 24, textAlign: 'center', color: T.muted }}>No trend data</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* ── DEPARTMENT ── */}
              {activeReport === 'dept' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                  <Card title="Ticket Load by Department" subtitle="Click a bar to filter by department">
                    <BarChart
                      data={data.byDept?.map(d => ({ label: d.department, value: d.count })) || []}
                      height={200}
                      onItemClick={(d) => goTickets({ department: d.label })}
                    />
                  </Card>
                  <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                      <thead>
                        <tr style={{ background: T.g50 }}>
                          {['#','Department','Ticket Count','% of Total'].map(h => (
                            <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 800, color: T.muted, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(data.byDept || []).map((d, i) => {
                          const total = data.totals?.total || 1;
                          const pct = Math.round((Number(d.count) / total) * 100);
                          const clickable = Number(d.count) > 0;
                          return (
                            <tr
                              key={i}
                              style={{ borderTop: `1px solid ${T.border}`, cursor: clickable ? 'pointer' : 'default' }}
                              onClick={clickable ? () => goTickets({ department: d.department }) : undefined}
                              title={clickable ? `View ${d.department} tickets` : undefined}
                            >
                              <td style={{ padding: '10px 16px', color: T.muted }}>{i + 1}</td>
                              <td style={{ padding: '10px 16px', fontWeight: 700, color: clickable ? T.accent : T.text, textDecoration: clickable ? 'underline' : 'none' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <div style={{ width: 10, height: 10, borderRadius: 3, background: COLORS[i % COLORS.length] }} />
                                  {d.department}
                                </div>
                              </td>
                              <td style={{ padding: '10px 16px', color: T.accent, fontWeight: 800 }}>{d.count}</td>
                              <td style={{ padding: '10px 16px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <div style={{ width: 80, height: 7, background: T.g50, borderRadius: 99, overflow: 'hidden' }}>
                                    <div style={{ width: `${pct}%`, height: '100%', background: COLORS[i % COLORS.length], borderRadius: 99 }} />
                                  </div>
                                  <span style={{ fontSize: 11, color: T.muted }}>{pct}%</span>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                        {!data.byDept?.length && <tr><td colSpan={4} style={{ padding: 24, textAlign: 'center', color: T.muted }}>No department data</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
